import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  Text,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Divider,
  Avatar,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import { updatePassword, updateUserAttributes } from 'aws-amplify/auth';
import { Icon } from 'src/components/atoms/Icon';
import { Button } from 'src/components/atoms/Button';
import { Switch } from 'src/components/atoms/Switch';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useAuthContext } from 'src/contexts/AuthContext';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { IS_DEV } from 'src/constants';

export const PersonalTab: React.FC = () => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userProfile, refreshAuthState } = useAuthContext();

  // Form states
  const [username, setUsername] = useState(userProfile?.name || '');
  const [email, setEmail] = useState(userProfile?.email || '');

  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileSaving, setIsProfileSaving] = useState(false);

  const { toastState, showToast, hideToast } = useCustomToast();

  // Notification states
  const [weeklyReports, setWeeklyReports] = useState(true);
  const [goalAchievements, setGoalAchievements] = useState(true);
  const [featureUpdates, setFeatureUpdates] = useState(true);
  const [marketingCommunications, setMarketingCommunications] = useState(true);

  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Update form when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setUsername(userProfile.name || '');
      setEmail(userProfile.email || '');
    }
  }, [userProfile]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        showToast(
          'Invalid file type',
          'Please upload a PNG or JPG file.',
          'error',
        );
        return;
      }
      // Validate file size (1MB)
      if (file.size > 1024 * 1024) {
        showToast(
          'File too large',
          'File size must be less than 1MB.',
          'error',
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Email is intentionally not editable. We only ever track/send the
  // username so that even if the disabled attribute is stripped in the
  // browser, the email can never be submitted.
  const hasProfileChanges = useMemo(() => {
    return username !== (userProfile?.name ?? '');
  }, [userProfile?.name, username]);

  const handleSaveProfile = async () => {
    if (!username.trim()) {
      showToast('Missing fields', 'Please provide your name.', 'error');
      return;
    }

    if (!hasProfileChanges) {
      showToast(
        'No changes detected',
        'Update your name before saving.',
        'error',
      );
      return;
    }

    setIsProfileSaving(true);
    try {
      await updateUserAttributes({
        userAttributes: {
          name: username.trim(),
        },
      });
      await refreshAuthState();

      showToast(
        'Profile updated',
        'Your name and email have been saved.',
        'success',
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating profile:', error);

      let errorMessage = 'Please try again or contact support.';
      if (
        error &&
        typeof error === 'object' &&
        'name' in error &&
        typeof (error as { name: string }).name === 'string'
      ) {
        const errorName = (error as { name: string }).name;
        if (errorName === 'AliasExistsException') {
          errorMessage = 'This email is already associated with another user.';
        }
      }

      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message?: string }).message === 'string'
      ) {
        errorMessage = (error as { message: string }).message;
      }

      showToast('Error updating profile', errorMessage, 'error');
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleSavePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast(
        'Missing fields',
        'Please fill in all password fields.',
        'error',
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast(
        'Passwords do not match',
        'New password and confirm password must be the same.',
        'error',
      );
      return;
    }

    if (newPassword.length < 8) {
      showToast(
        'Password too short',
        'Password must be at least 8 characters long.',
        'error',
      );
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      showToast(
        'Password updated successfully!',
        'Your password has been changed.',
        'success',
      );

      // Clear password fields on success
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Error updating password:', error);

      let errorMessage = 'Please try again or contact support.';

      if (error && typeof error === 'object' && 'name' in error) {
        if (error.name === 'NotAuthorizedException') {
          errorMessage = 'Current password is incorrect.';
        } else if (error.name === 'InvalidPasswordException') {
          errorMessage = 'Password does not meet requirements.';
        } else if (error.name === 'LimitExceededException') {
          errorMessage = 'Too many attempts. Please try again later.';
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }

      showToast('Error updating password', errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="flex-start">
      <Box
        width="100%"
        maxWidth={'460px'}
        gap={6}
        display="flex"
        flexDirection="column">
        {IS_DEV && (
          <HStack spacing={6} align="flex-start">
            <Box position="relative">
              {profileImage ? (
                <Avatar src={profileImage} size="lg" borderRadius="full" />
              ) : (
                <Avatar
                  size="lg"
                  name={userProfile?.name}
                  borderRadius="full"
                />
              )}
            </Box>
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="md" fontWeight="semibold" color={textColor}>
                Profile Photo
              </Text>
              <Text fontSize="sm" color="content.muted">
                PNG or JPG, max 1MB
              </Text>
              <HStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <HStack
                  as="button"
                  onClick={() => fileInputRef.current?.click()}
                  cursor="pointer"
                  _hover={{ color: 'blue.600' }}>
                  <Icon iconName="upload-data" boxSize={4} />
                  <Text fontSize="sm" fontWeight="medium">
                    Upload
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </HStack>
        )}

        {/* Username Field */}
        <FormControl>
          <FormLabel
            fontSize="16px"
            fontWeight={500}
            color="content.title"
            mb={3}>
            Username
          </FormLabel>
          <Input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
            bg="surface.panel"
            border="1px solid"
            borderColor="surface.border"
            borderRadius="md"
            py={3}
            px={4}
            fontSize="16px"
            _placeholder={{ color: 'content.muted' }}
            _focus={{
              borderColor: 'icon.green',
              boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
            }}
          />
        </FormControl>

        {/* Email Field */}
        <FormControl>
          <FormLabel
            fontSize="16px"
            fontWeight={500}
            color="content.title"
            mb={3}>
            Email
          </FormLabel>
          <Input
            type="email"
            value={email}
            isDisabled
            isReadOnly
            placeholder="Enter email"
            bg="surface.panel"
            border="1px solid"
            borderColor="surface.border"
            borderRadius="md"
            py={3}
            px={4}
            fontSize="16px"
            cursor="not-allowed"
            _placeholder={{ color: 'content.muted' }}
            _disabled={{
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
          />
        </FormControl>

        <Box width="100%" maxWidth={'460px'}>
          <Button
            variant="primary"
            size={{ base: 'md', md: 'lg' }}
            px={{ base: 8, md: 16 }}
            py={{ base: 4, md: 6 }}
            width="100%"
            buttonName="save-profile-settings"
            onClick={handleSaveProfile}
            isLoading={isProfileSaving}
            disabled={isProfileSaving || !hasProfileChanges}>
            Save Profile
          </Button>
        </Box>
      </Box>

      {/* Divider */}
      <Divider borderColor={borderColor} />

      {/* Change Password Section */}
      <Box width="100%" maxWidth={'460px'}>
        <Text fontSize="md" fontWeight="semibold" color={textColor} mb={2}>
          Change Password
        </Text>
        <Text fontSize="sm" color="content.muted" mb={6}>
          Ensure your account is using a secure password.
        </Text>

        <VStack spacing={6} align="stretch">
          {/* Current Password */}
          <FormControl>
            <FormLabel
              fontSize="16px"
              fontWeight={500}
              color="content.title"
              mb={3}>
              Current Password
            </FormLabel>
            <Input
              type="password"
              placeholder="Enter current password"
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              py={3}
              px={4}
              fontSize="16px"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </FormControl>

          {/* New Password */}
          <FormControl>
            <FormLabel
              fontSize="16px"
              fontWeight={500}
              color="content.title"
              mb={3}>
              New Password
            </FormLabel>
            <Input
              type="password"
              placeholder="Enter new password"
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              py={3}
              px={4}
              fontSize="16px"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </FormControl>

          {/* Confirm New Password */}
          <FormControl>
            <FormLabel
              fontSize="16px"
              fontWeight={500}
              color="content.title"
              mb={3}>
              Confirm New Password
            </FormLabel>
            <Input
              type="password"
              placeholder="Confirm new password"
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              py={3}
              px={4}
              fontSize="16px"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </FormControl>
          {/* Save Button */}
          <Box width="100%" maxWidth={'460px'}>
            <Button
              variant="primary"
              size={{ base: 'md', md: 'lg' }}
              px={{ base: 8, md: 16 }}
              py={{ base: 4, md: 6 }}
              width="100%"
              buttonName="save-personal-settings"
              onClick={handleSavePassword}
              isLoading={isLoading}
              disabled={isLoading}>
              Save
            </Button>
          </Box>
        </VStack>
      </Box>

      {IS_DEV && (
        <>
          {/* Divider */}
          <Divider borderColor={borderColor} />

          {/* Notifications Section */}
          <Box maxWidth={'1460px'}>
            <Text fontSize="md" fontWeight="semibold" color={textColor} mb={2}>
              Notifications
            </Text>
            <Text fontSize="sm" color="content.muted" mb={6}>
              Manage how you receive notifications and updates from our
              platform.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {/* Email Notifications Column */}
              <VStack align="flex-start" spacing={6}>
                <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                  Email Notifications
                </Text>

                {/* Weekly Reports */}
                <Box width="100%">
                  <HStack justify="space-between" width="100%" gap={20}>
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        Weekly Reports
                      </Text>
                      <Text fontSize="xs" color="content.muted">
                        Receive a summary of your analytics every week
                      </Text>
                    </VStack>
                    <Switch
                      isChecked={weeklyReports}
                      onChange={(e) => setWeeklyReports(e.target.checked)}
                      colorScheme="green"
                      size="md"
                    />
                  </HStack>
                </Box>

                {/* Goal Achievements */}
                <Box width="100%">
                  <HStack justify="space-between" width="100%">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        Goal Achievements
                      </Text>
                      <Text fontSize="xs" color="content.muted">
                        Get notified when you reach your goals
                      </Text>
                    </VStack>
                    <Switch
                      isChecked={goalAchievements}
                      onChange={(e) => setGoalAchievements(e.target.checked)}
                      colorScheme="green"
                      size="md"
                    />
                  </HStack>
                </Box>
              </VStack>

              {/* Product Updates Column */}
              <VStack align="flex-start" spacing={6}>
                <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                  Product Updates
                </Text>

                {/* Feature Updates */}
                <Box width="100%">
                  <HStack justify="space-between" width="100%">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        Feature Updates
                      </Text>
                      <Text fontSize="xs" color="content.muted">
                        Learn about new features and improvements
                      </Text>
                    </VStack>
                    <Switch
                      isChecked={featureUpdates}
                      onChange={(e) => setFeatureUpdates(e.target.checked)}
                      colorScheme="green"
                      size="md"
                    />
                  </HStack>
                </Box>

                {/* Marketing Communications */}
                <Box width="100%">
                  <HStack justify="space-between" width="100%">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        Marketing Communications
                      </Text>
                      <Text fontSize="xs" color="content.muted">
                        Get updates about promotions and events
                      </Text>
                    </VStack>
                    <Switch
                      isChecked={marketingCommunications}
                      onChange={(e) =>
                        setMarketingCommunications(e.target.checked)
                      }
                      colorScheme="green"
                      size="md"
                    />
                  </HStack>
                </Box>
              </VStack>
            </SimpleGrid>
          </Box>
        </>
      )}

      {/* Custom Toast */}
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </VStack>
  );
};
